import { useState, useEffect, useRef } from "react";
import { supabase } from "@/lib/supabase";
import * as XLSX from "xlsx";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

interface Affiliate {
  id: string; user_id: string; code: string; name: string; email: string;
  commission_percent: number; commission_months: number; active: boolean;
}
interface Commission {
  id: string; affiliate_id: string; user_id: string; plan_id: string;
  amount_paid: number; commission_amount: number; cycle_number: number;
  paid_to_affiliate: boolean; created_at: string;
}
interface UserOpt { id: string; name: string; email: string; }

export default function AffiliatesTab() {
  const [affiliates, setAffiliates] = useState<Affiliate[]>([]);
  const [commissions, setCommissions] = useState<Commission[]>([]);
  const [loading, setLoading] = useState(true);

  const [form, setForm] = useState<any>({ id: null, user_id: "", code: "", commission_percent: 20, commission_months: 6, active: true });
  const [selectedUser, setSelectedUser] = useState<UserOpt | null>(null);
  const [userQuery, setUserQuery] = useState("");
  const [userResults, setUserResults] = useState<UserOpt[]>([]);
  const [showResults, setShowResults] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const searchTimeout = useRef<ReturnType<typeof setTimeout>>();

  async function load() {
    setLoading(true);
    const { data: affs } = await supabase.from("affiliates").select("*").order("created_at", { ascending: false });
    const { data: comms } = await supabase.from("affiliate_commissions").select("*").order("created_at", { ascending: false });
    setAffiliates(affs || []);
    setCommissions(comms || []);
    setLoading(false);
  }
  useEffect(() => { load(); }, []);

  const [exportPeriod, setExportPeriod] = useState("90");
  const [exporting, setExporting] = useState(false);
  async function downloadReport() {
    setExporting(true);
    const days = exportPeriod === "all" ? null : Number(exportPeriod);
    const { data, error } = await supabase.rpc("admin_commissions_report", { p_days: days });
    setExporting(false);
    if (error) { alert("Erro ao gerar relatório."); return; }
    const rows = (data as any[]) || [];
    if (rows.length === 0) { alert("Nenhuma comissão no período selecionado."); return; }
    const sheet = rows.map((r) => ({
      "Afiliado": r.afiliado_nome,
      "E-mail Afiliado": r.afiliado_email,
      "Código": r.afiliado_codigo,
      "% Comissão": r.afiliado_percent,
      "Duração (meses)": r.afiliado_meses,
      "Indicado": r.indicado_nome,
      "E-mail Indicado": r.indicado_email,
      "Plano": r.plano,
      "Valor Pago (R$)": Number(r.valor_pago),
      "Comissão (R$)": Number(r.comissao),
      "Ciclo": r.ciclo,
      "Status": r.status,
      "Data": new Date(r.data).toLocaleDateString("pt-BR"),
    }));
    const ws = XLSX.utils.json_to_sheet(sheet);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Comissões");
    const label = exportPeriod === "all" ? "completo" : exportPeriod + "dias";
    XLSX.writeFile(wb, `comissoes-afiliados-${label}.xlsx`);
  }

  function searchUsers(q: string) {
    setUserQuery(q);
    clearTimeout(searchTimeout.current);
    if (q.trim().length < 2) { setUserResults([]); return; }
    searchTimeout.current = setTimeout(async () => {
      const { data } = await supabase.rpc("admin_search_users", { p_query: q.trim() });
      setUserResults((data as UserOpt[]) || []);
      setShowResults(true);
    }, 350);
  }

  function pickUser(u: UserOpt) {
    setSelectedUser(u);
    setForm({ ...form, user_id: u.id });
    setUserQuery(`${u.name} (${u.email})`);
    setShowResults(false);
  }

  function resetForm() {
    setForm({ id: null, user_id: "", code: "", commission_percent: 20, commission_months: 6, active: true });
    setSelectedUser(null); setUserQuery(""); setUserResults([]);
  }

  async function saveAffiliate() {
    if (!form.user_id) { setMsg("Selecione um usuário."); return; }
    if (!form.code.trim()) { setMsg("Informe o código."); return; }
    setSaving(true); setMsg("");
    const { data, error } = await supabase.rpc("admin_upsert_affiliate", {
      p_id: form.id, p_user_id: form.user_id, p_code: form.code.trim(),
      p_percent: Number(form.commission_percent), p_months: Number(form.commission_months),
      p_active: form.active,
    });
    setSaving(false);
    if (error || !(data as any)?.ok) { setMsg((data as any)?.error || "Erro ao salvar."); return; }
    resetForm(); load();
  }

  function editAffiliate(a: Affiliate) {
    setForm({ id: a.id, user_id: a.user_id, code: a.code, commission_percent: a.commission_percent, commission_months: a.commission_months, active: a.active });
    setSelectedUser({ id: a.user_id, name: a.name, email: a.email });
    setUserQuery(`${a.name} (${a.email})`);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  async function markPaid(id: string) {
    const { data, error } = await supabase.rpc("admin_mark_commission_paid", { p_commission_id: id });
    if (error || !(data as any)?.ok) { alert("Erro ao marcar como paga."); return; }
    load();
  }

  const affName = (id: string) => affiliates.find(a => a.id === id)?.name || "—";
  const pendentes = commissions.filter(c => !c.paid_to_affiliate);
  const totalPendente = pendentes.reduce((s, c) => s + Number(c.commission_amount), 0);
  const baseUrl = "https://www.agroaluga.com.br/?ref=";

  return (
    <div className="space-y-8">
      {/* Formulário */}
      <div className="bg-card border border-border rounded-lg p-5">
        <h3 className="font-heading font-bold mb-4">{form.id ? "Editar afiliado" : "Novo afiliado"}</h3>

        {/* Seletor de usuário */}
        <div className="relative mb-3">
          <label className="text-xs text-muted-foreground">Usuário (deve ter conta no site)</label>
          <Input
            value={userQuery}
            onChange={(e) => searchUsers(e.target.value)}
            onFocus={() => userResults.length > 0 && setShowResults(true)}
            placeholder="Buscar por nome ou e-mail..."
            className="h-9 text-sm"
            disabled={!!form.id}
          />
          {showResults && userResults.length > 0 && (
            <div className="absolute z-20 left-0 right-0 bg-card border border-border rounded-md shadow-lg mt-1 max-h-48 overflow-y-auto">
              {userResults.map((u) => (
                <button key={u.id} type="button" onClick={() => pickUser(u)}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-secondary border-b border-border/50 last:border-0">
                  <span className="font-medium">{u.name}</span>
                  <span className="text-xs text-muted-foreground block">{u.email}</span>
                </button>
              ))}
            </div>
          )}
          {selectedUser && <p className="text-xs text-primary mt-1">✓ {selectedUser.name} — {selectedUser.email}</p>}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="text-xs text-muted-foreground">Código (link)</label>
            <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="ex: joao" className="h-9 text-sm" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Comissão (%)</label>
            <Input type="number" value={form.commission_percent} onChange={(e) => setForm({ ...form, commission_percent: e.target.value })} className="h-9 text-sm" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Duração (meses)</label>
            <Input type="number" value={form.commission_months} onChange={(e) => setForm({ ...form, commission_months: e.target.value })} className="h-9 text-sm" />
          </div>
        </div>
        <div className="flex items-center gap-3 mt-3">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="h-4 w-4 accent-primary" />
            Ativo
          </label>
          <Button onClick={saveAffiliate} disabled={saving} className="ml-auto bg-primary text-primary-foreground">
            {saving ? "Salvando..." : form.id ? "Atualizar" : "Cadastrar"}
          </Button>
          {form.id && <Button variant="outline" onClick={resetForm}>Cancelar</Button>}
        </div>
        {msg && <p className="text-xs text-destructive mt-2">{msg}</p>}
      </div>

      {/* Lista de afiliados */}
      <div>
        <h3 className="font-heading font-bold mb-3">Afiliados ({affiliates.length})</h3>
        {loading ? <p className="text-sm text-muted-foreground">Carregando...</p> : (
          <div className="space-y-2">
            {affiliates.map((a) => (
              <div key={a.id} className="bg-card border border-border rounded-lg p-3 flex items-center gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm">{a.name} {!a.active && <span className="text-xs text-destructive">(inativo)</span>}</p>
                  <p className="text-xs text-muted-foreground">{a.commission_percent}% por {a.commission_months} meses · {a.email}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <code className="text-xs bg-secondary px-2 py-0.5 rounded">{baseUrl}{a.code}</code>
                    <button onClick={() => navigator.clipboard.writeText(baseUrl + a.code)} className="text-xs text-primary hover:underline">copiar</button>
                  </div>
                </div>
                <Button size="sm" variant="outline" onClick={() => editAffiliate(a)}>Editar</Button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Comissões */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading font-bold">Comissões</h3>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm">Pendente: <strong className="text-primary">R$ {totalPendente.toFixed(2).replace(".", ",")}</strong></span>
            <select value={exportPeriod} onChange={(e) => setExportPeriod(e.target.value)}
              className="h-8 text-xs rounded-md border border-border bg-background px-2">
              <option value="30">Último mês</option>
              <option value="60">Últimos 2 meses</option>
              <option value="90">Últimos 3 meses</option>
              <option value="365">Último ano</option>
              <option value="all">Completo</option>
            </select>
            <Button size="sm" onClick={downloadReport} disabled={exporting} className="bg-primary text-primary-foreground text-xs">
              {exporting ? "Gerando..." : "Baixar XLSX"}
            </Button>
          </div>
        </div>
        {commissions.length === 0 ? <p className="text-sm text-muted-foreground">Nenhuma comissão ainda.</p> : (
          <div className="space-y-2">
            {commissions.map((c) => (
              <div key={c.id} className={`border rounded-lg p-3 flex items-center gap-3 flex-wrap text-sm ${c.paid_to_affiliate ? "bg-secondary/40 border-border" : "bg-card border-border"}`}>
                <div className="flex-1 min-w-0">
                  <p className="font-medium">{affName(c.affiliate_id)} · ciclo {c.cycle_number}</p>
                  <p className="text-xs text-muted-foreground">
                    Pago: R$ {Number(c.amount_paid).toFixed(2).replace(".", ",")} → comissão R$ {Number(c.commission_amount).toFixed(2).replace(".", ",")} · {new Date(c.created_at).toLocaleDateString("pt-BR")}
                  </p>
                </div>
                {c.paid_to_affiliate
                  ? <span className="text-xs font-semibold text-green-700">✓ Paga</span>
                  : <Button size="sm" onClick={() => markPaid(c.id)} className="bg-primary text-primary-foreground">Marcar paga</Button>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
